import { resolveId } from '@medplum/core'
import { CarePlanSignatureTask } from '@seen/core/models/tasks/signature/carePlanSignatureTask'
import { SDRSignatureTask } from '@seen/core/models/tasks/signature/sdrSignatureTask'
import { DocumentReviewTask } from '@seen/core/models/tasks/standard/documentReviewTask'
import { QueuedEditsReviewTask } from '@seen/core/models/tasks/standard/queuedEditsReviewTask'
import type { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { isOfType } from '@seen/core/utils'
import { Separator } from '@seen/ui'
import type { ReactNode } from 'react'
import { Activity } from '#src/components/activity/Activity.tsx'
import { OrderPage } from '#src/components/order/OrderPage.tsx'
import { QueuedEditsReviewTaskCard } from '#src/components/queued-edits/QueuedEditsReviewTaskCard.tsx'
import { CarePlanCard } from '#src/pages/task/CarePlanCard.tsx'
import { DocumentReviewCard } from '#src/pages/task/DocumentReviewCard.tsx'
import { SDRCard } from '#src/pages/task/SDRCard.tsx'
import { TaskActivity } from '#src/pages/task/TaskActivity.tsx'
import { TaskPageContent } from '#src/pages/task/TaskPageContent.tsx'

/* Renders the task's focus resource (order, care plan, SDR, document review).
 * Falls back to full TaskPageContent when the focus type is unrecognized. */
export function TaskFocusContent({
  task,
  includeActivity = false,
}: {
  task: StandardTask
  includeActivity?: boolean
}): ReactNode {
  const fhirTask = task.toFhirResource()

  const activitySection = includeActivity && (
    <>
      <Separator className="my-6" />
      <Activity resource={task} subject={task.participant}>
        <TaskActivity task={task} />
      </Activity>
    </>
  )

  /* ServiceRequest (Order) — OrderPage already renders its own Activity */
  if (isOfType(task.focus, 'ServiceRequest')) {
    return <OrderPage orderId={resolveId(task.focus)} hideMaximize />
  }

  /* CarePlan signature */
  if (
    isOfType(task.focus, 'CarePlan') &&
    CarePlanSignatureTask.isInstance(fhirTask)
  ) {
    return (
      <>
        <CarePlanCard
          carePlan={task.focus}
          task={CarePlanSignatureTask.fromFhirResource(fhirTask)}
        />
        {activitySection}
      </>
    )
  }

  /* SDR signature */
  if (isOfType(task.focus, 'Task') && SDRSignatureTask.isInstance(fhirTask)) {
    return (
      <>
        <SDRCard
          sdr={task.focus}
          task={SDRSignatureTask.fromFhirResource(fhirTask)}
        />
        {activitySection}
      </>
    )
  }

  /* Document review */
  if (DocumentReviewTask.isInstance(fhirTask)) {
    return (
      <>
        <DocumentReviewCard
          task={DocumentReviewTask.fromFhirResource(fhirTask)}
        />
        {activitySection}
      </>
    )
  }

  /* Queued-edits review (human-in-the-loop bundle approval) */
  if (QueuedEditsReviewTask.isInstance(fhirTask)) {
    return (
      <div className="p-4">
        <QueuedEditsReviewTaskCard
          task={QueuedEditsReviewTask.fromFhirResource(fhirTask)}
        />
        {activitySection}
      </div>
    )
  }

  return <TaskPageContent task={task} stickyHeader={false} />
}
